import { useState } from 'react';
import useSignupData from '@assetron/common/hooks/useSignupData';
import { Box, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useNavigation } from '../../navigation/useNavigation';
import { ResponseStatusCode } from '@assetron/common/types/responseStatus';
import { useLocalization } from '@assetron/common/hooks/useLocalization';
import { resetPasswordEmailStatus } from '@assetron/common/api/auth';
import {
  API_ERROR_CODES,
  WHITESPACE,
} from '@assetron/common/constants/constant';
import validateEmail from '@assetron/common/utils/validations/validateEmail';
import AssetronButton from '@assetron/web-common/components/common/AssetronButton';
import ErrorMessage from '@assetron/web-common/components/common/ErrorMessage';
import useSigninEnterPasswordContentStyles from '../signin/styles/useSigninEnterPasswordContentStyles';
import AssetronHeading from '@assetron/web-common/components/common/AssetronHeading';
import useDocumentTitle from '@assetron/web-common/components/common/useDocumentTitle';
import AuthAssetronInputField from '../common/AuthAssetronInputField';
import { notifyToaster } from '@assetron/web-common/components/Toaster/ToasterService';
import apiErrorHandler from '@assetron/common/utils/apiErrorHandler';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';

function EnterEmailContent() {
  const { userEmail, setUserEmail } = useSignupData();
  const [email, setEmail] = useState(userEmail);
  const { navigateToResetPasswordEmailVerification } = useNavigation();
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const { t } = useLocalization();
  useDocumentTitle(t('resetPassword.pageTitle'));

  const styles = useSigninEnterPasswordContentStyles();

  const resetPasswordEmailStatusMutation = useMutation({
    mutationFn: resetPasswordEmailStatus,
    onSuccess: (data) => {
      const { status: statusCode } = data;
      if (statusCode === ResponseStatusCode.Accepted) {
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_EMAIL_SUCCESS, {
          email_length: email.length,
        });
        setUserEmail(email);
        navigateToResetPasswordEmailVerification();
      } else if (statusCode === ResponseStatusCode.NotFound) {
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_EMAIL_FAILURE, {
          status_code: statusCode,
        });
        notifyToaster({
          type: 'error',
          message: t('authErrors.noUserFound'),
        });
        setIsEmailValid(false);
      } else {
        trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_EMAIL_FAILURE, {
          status_code: statusCode,
        });
        notifyToaster({
          type: 'error',
          message: t('apiErrorText'),
        });
      }
    },
    onError: (error: unknown) => {
      trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_EMAIL_FAILURE, {
        status_code: error?.response?.status || error?.status,
      });
      // Type guard for error with status property
      const errorStatus = error?.response?.status || error?.status;
      if (errorStatus === API_ERROR_CODES.NOT_FOUND) {
        setIsEmailValid(false);
      } else {
        apiErrorHandler(error, {}, t('apiErrorText'));
      }
    },
  });

  const handleSubmit = async () => {
    trackGaEvent(GA_EVENTS.AUTH_RESET_PASSWORD_EMAIL_SUBMIT, {
      email_length: email.length,
    });
    if (validateEmail(email)) {
      resetPasswordEmailStatusMutation.mutate({ email });
    } else {
      setIsEmailValid(false);
    }
  };

  return (
    <Box sx={styles.rootEnterEmail}>
      <Box sx={styles.emailContainerResetPassword}>
        <AssetronHeading title={t('resetPassword.enterEmail')} />
        <Typography variant="body_14_regular" sx={styles.emailText}>
          {t('resetPassword.enterEmailText')}
        </Typography>
      </Box>
      <Box sx={styles.passwordContainer}>
        <AuthAssetronInputField
          label={t('signInLandingPage.emailLabel')}
          value={email}
          dataTestId="reset-password-email-input"
          onTextChange={(e) => {
            setIsEmailValid(true);
            const { value } = e.target;
            setEmail(
              value.startsWith(WHITESPACE)
                ? value.trim().toLowerCase()
                : value.toLowerCase(),
            );
          }}
          destructive={!isEmailValid}
          errorMessage={
            !isEmailValid && (
              <ErrorMessage
                message={t('signInLandingPage.accountDoesntExist')}
              />
            )
          }
        />
      </Box>
      <Box sx={styles.button}>
        <AssetronButton
          hierarchy="primary"
          size="md"
          onClick={handleSubmit}
          isLoading={resetPasswordEmailStatusMutation.isLoading}
          disabled={!email || !validateEmail(email) || !isEmailValid}
          dataTestId="reset-password-email-next-button"
        >
          <Typography variant="body_14_medium">
            {t('resetPassword.next')}
          </Typography>
        </AssetronButton>
      </Box>
    </Box>
  );
}

export default EnterEmailContent;
